import * as companyService from "../../services/company";
import * as settings from "../../services/settings";

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
  notification,
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission, useUser } from "../../hooks/usePermission";

import AuthGroup from "../../../configs/authgroup";
import { ModalDelete } from "../../Components/model/modal/delete";
import dayjs from "dayjs";
import { exportComponent } from "../../Components/model/modal/export";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { useTranslation } from "react-i18next";

const initPage = { limit: 10, page: 1 };
const { Link } = Typography;
const { RangePicker } = DatePicker;

export const CompanyPage = () => {
  const hasInitialized = useRef(false);
  const { accessToken } = useAccessToken();
  const { companyId, saleId } = useUser();
  const { hasPermission, hasGroup, isCompanyFullAccess } = usePermission();
  const [fromSearch] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("company", "buttons");
  const [search, setSearch] = useState(initPage);
  const [companyData, setCompanyData] = useState([]);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});
  const [dateRange, setDateRange] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowsValues, setSelectedRowsValues] = useState([]);
  const [countSelect, setCountSelect] = useState(0);
  const [isOpenExport, setIsOpenExport] = useState(false);
  const [isExportAll, setIsExportAll] = useState(false);

  // search company and sale
  const [company, setCompany] = useState([]);
  const navigate = useNavigate();
  const { Title, Text } = Typography;
  const [total, setTotal] = useState(0);

  const [api, contextHolder] = notification.useNotification();

  const defaultUserData = {
    company_id: companyId,
    sale_id: saleId,
  };

  const [tempSearch, setTempSearch] = useState();
  const [mappingCompanyId, setMappingCompanyId] = useState(defaultUserData?.company_id);

  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );

  const onDeleteCompany = async (data) => {
    let prepareDeleteCompany = {
      id: data.id,
    };
    setLoading(true);
    try {
      const response = await companyService.deleteCompany(accessToken, prepareDeleteCompany);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{response}</>);
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchCompany({ ...search });
      setLoading(false);
    } catch (err) {
      console.error("[CompanyPage] onDeleteCompany: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  const fetchCompany = useCallback(
    async (param) => {
      setLoading(true);
      try {
        const data = await companyService.getCompany(accessToken, param);
        setTotal(data?.totalCount);
        setCompanyData(data.queryCompany);
        setSearch({ ...param });
        //  Save to localStorage
        const updatedSearch = { ...search, values: param };
        localStorage.setItem("companySearchParams", JSON.stringify(updatedSearch));
      } catch (err) {
        console.error("[CompanyPage] fetchCompany: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  const onSelectAll = async () => {
    setCountSelect(total);
    setIsExportAll(true);
    setIsOpenExport(total.length > 0 ? true : false);
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
  };

  const onSearch = async (value) => {
    const { search, search_date, search_company_id, search_sale_id, search_credit, ...data } = value;
    let start_date;
    let end_date;

    if (data && search_date) {
      ((start_date = dateRange[0] || undefined), (end_date = dateRange[1] || undefined));
    }

    const searchText = search != undefined ? search.trim() : null;
    const values = {
      start_date: start_date,
      end_date: end_date,
      company_id: search_company_id !== "all" ? search_company_id : undefined,
      sale_id: search_sale_id !== "all" ? search_sale_id : undefined,
      company_code: searchText,
      company_name: searchText,
      telephone: searchText,
      is_credit: search_credit,
      is_superuser: isCompanyFullAccess,
    };

    setTempSearch(value);
    await fetchCompany({ ...values, page: value.page ?? initPage.page, limit: value.limit ?? initPage.limit });
  };

  const rowSelection = {
    selectedRowKeys,
    columnWidth: "2%",
    onChange: (selectedRowKeys, value) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRowsValues(value);
      setCountSelect(value.length);
      setIsOpenExport(selectedRowKeys.length > 0 ? false : true);
      setIsExportAll(false);
    },
  };

  const updateStatus = async (record, event) => {
    setLoading(true);
    try {
      const body = {
        id: record.id,
        is_active: event,
      };
      const response = await companyService.updateCompanyStatus(accessToken, body);
      if (response != undefined) {
        openNotificationWithIcon(settings.NotificationType.SUCCESS, "Success.", "Update status success");
      }

      fetchCompany({ ...search });
    } catch (err) {
      console.error(err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, "ERROR.", `${message}`);
    }
    setLoading(false);
  };

  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dateStrings);
  };

  const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  const disableFutureDates = (current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  const onFinish = async () => {
    const values = fromSearch.getFieldsValue();
    try {
      setTempSearch(values);
      setLoading(true);
      await onSearch(values);
    } finally {
      setLoading(false);
    }
  };

  const onReset = async () => {
    fromSearch.resetFields();
    try {
      setLoading(true);
      setCountSelect(0);
      await fetchCompany({ ...defaultUserData, ...initPage });
    } finally {
      setLoading(false);
    }
  };

  const getCompanyList = async () => {
    try {
      setLoading(true);
      const companyList = await companyService.getCompanysList(accessToken);
      setCompany(companyList);
    } catch (err) {
      console.error(err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, "ERROR.", `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const getCompanyById = useCallback(
    async (id) => {
      try {
        let companyList = [];
        setLoading(true);
        const data = await companyService.getCompanyById(accessToken, id);
        companyList.push(data);
        setCompany(companyList);
      } catch (err) {
        console.error("[CustomerPage] fetchCustomerCompany: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  const mappingCompanyOption = async () => {
    if (isCompanyFullAccess || !defaultUserData?.company_id) {
      await getCompanyList();
    } else {
      await getCompanyById(defaultUserData?.company_id);
    }
  };

  const optionCompanySelection = (companies = []) => {
    const hasMultiple = companies.length > 1;

    return [
      ...(hasMultiple ? [{ label: "All Company", value: "all" }] : []),
      ...companies.map((item, index) => ({
        label: `${item.company_name} (${item.company_code})`,
        value: item.id,
        key: `company-${index}-${item.id}`,
      })),
    ];
  };

  const optionSaleSelection = (companies = []) => {
    return [
      { label: "All Sale", value: "all" },
      ...companies
        .filter((company) => company.id === mappingCompanyId)
        .flatMap((company) =>
          (company.app_sale || []).map((sale, sIndex) => ({
            label: `${sale.sale_name} (${company.company_code}-${sale.sale_name})`,
            value: sale.id,
            key: `sale-${company.id}-${sIndex}-${sale.id}`,
          }))
        ),
    ];
  };

  const handleChangeCompany = (value, _) => {
    setMappingCompanyId(value);
  };

  useEffect(() => {
    localStorage.removeItem("editCompanyData");
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;

    setIsOpenExport(true);
    setCountSelect(0);
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
    (async () => {
      await Promise.all([fetchCompany({ ...defaultUserData, ...initPage }), mappingCompanyOption()]);
    })();
  }, [accessToken]);

  const onExport = async () => {
    // Define custom headers with corresponding keys
    const headerMap = [
      { label: t("Company_Export_Excel.Company_Code"), key: "company_code" },
      { label: t("Company_Export_Excel.Company_Name"), key: "company_name" },
      {
        label: t("Company_Export_Excel.Company_Address"),
        key: "company_address",
      },
      { label: t("Company_Export_Excel.Telephone"), key: "telephone" },
      { label: t("Company_Export_Excel.E_Mail"), key: "email" },
      { label: t("Company_Export_Excel.Tax_Id"), key: "tax_id" },
      { label: t("Company_Export_Excel.Is_Credit"), key: "is_credit" },
      {
        label: t("Company_Export_Excel.Bank_Account_Company_Bill"),
        key: "bank_account_company_bill",
      },
      {
        label: t("Company_Export_Excel.Bank_Account_Customer_Bill"),
        key: "bank_account_customer_bill",
      },
      { label: t("Company_Export_Excel.Start_Date"), key: "start_date" },
    ];
    const fileName = `company-${dayjs().format("YYYYMMDD")}${dayjs().format("HHmmss")}`;
    let formattedData;
    setIsOpenExport(true);
    if (isExportAll) {
      try {
        let startDate = dateRange[0];
        let endDate = dateRange[1];

        const params = {
          page: 1,
          limit: total,
          start_date: startDate,
          end_date: endDate,
        };
        const response = await companyService.exportCompany(accessToken, params);
        // สร้างลิงก์สำหรับดาวน์โหลดไฟล์
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName + ".xlsx"); // ชื่อไฟล์
        document.body.appendChild(link);
        link.click();
        // ทำความสะอาดลิงก์
        link.parentNode.removeChild(link);
        setIsOpenExport(false);
      } catch (err) {
        console.error("[CompanyPage] onExport (all): ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setIsOpenExport(false);
      }
    } else {
      try {
        formattedData = selectedRowsValues.map((item) => {
          return {
            company_code: item.company_code || "-",
            company_name: item.company_name || "-",
            company_address: item.company_address || "-",
            telephone: item.telephone || "-",
            email: item.email || "-",
            tax_id: item.tax_id || "-",
            is_credit: item.is_credit ? "เครดิต" : "เงินสด",
            bank_account_company_bill: item.bank_account_company_bill || "-",
            bank_account_customer_bill: item.bank_account_customer_bill || "-",
            start_date: item.created_at ? dayjs(item.created_at).format("YYYY-MM-DD") : "-",
          };
        });
        exportComponent({ formattedData, fileName, headerMap });
      } catch (err) {
        console.error("[CompanyPage] onExport (selected): ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setIsOpenExport(false);
      }
    }
  };

  const columns = [
    {
      title: t("Company_Table.Table_Id"),
      width: "3%",
      dataIndex: "id",
      render: (_, row) => {
        return (
          <Link
            style={{ cursor: "pointer" }}
            onClick={() => {
              localStorage.setItem("companySearchParams", JSON.stringify(search));
              localStorage.setItem(
                "editCompanyData",
                JSON.stringify({
                  type: "EDIT",
                  companyData: row,
                })
              );
              navigate("/company/create?tab=1");
            }}
          >
            {row.id}
          </Link>
        );
      },
    },
    {
      title: t("Company_Table.Table_Code"),
      width: "5%",
      dataIndex: "company_code",
      render: (_, row) => {
        return (
          <p
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/company/create", {
                state: { type: "EDIT", companyData: row },
              });
            }}
          >
            {row.company_code}
          </p>
        );
      },
    },
    {
      title: t("Company_Table.Table_Name"),
      width: "8%",
      dataIndex: "company_name",
    },
    {
      title: t("Company_Table.Table_Sale_Count"),
      dataIndex: "app_sale",
      width: "4%",
      render: (_, record) => {
        const app_sale = record._count.app_sale || "";
        return <p>{app_sale}</p>;
      },
    },
    {
      title: t("Company_Table.Table_Customer_Count"),
      width: "4%",
      dataIndex: "app_customer",
      render: (_, record) => {
        const app_customer = record._count.app_customer || "";
        return <p>{app_customer}</p>;
      },
    },
    {
      title: t("Company_Table.Table_Route"),
      width: "8%",
      dataIndex: "app_companyroute",
      render: (_, record) => {
        return (
          <span>
            {record.app_companyroute.map((value, index) => {
              let destination = value.app_warehouse_app_companyroute_destination_idToapp_warehouse.warehouse_code;
              let origin = value.app_warehouse_app_companyroute_origin_idToapp_warehouse.warehouse_code;

              const formula = `${record.company_code}(${origin}->${destination})`;
              return (
                <span key={index}>
                  {formula}
                  {index < record.app_companyroute.length - 1 ? ", " : ""}
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("Company_Table.Table_Formula"),
      width: "8%",
      dataIndex: "app_companyroute",
      render: (_, record) => {
        return (
          <span>
            {record.app_companyroute.map((value, index) => {
              let destination = value.app_warehouse_app_companyroute_destination_idToapp_warehouse.warehouse_code;
              let origin = value.app_warehouse_app_companyroute_origin_idToapp_warehouse.warehouse_code;

              const formula = `${record.company_code}(${origin}->${destination})`;
              return (
                <span key={index}>
                  {formula}
                  {index < record.company_code.length - 1 ? ", " : ""}
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("Company_Table.Table_Is_Credit"),
      width: "3%",
      dataIndex: "is_credit",
      render: (_, record) => (
        <p style={{ color: record.is_credit ? "green" : "red" }}>
          <CheckCircleOutlined />
        </p>
      ),
    },
    {
      title: t("Company_Table.Table_Email_Address"),
      width: "5%",
      dataIndex: "email",
      render: (_, record) => record.email || "-",
    },
    {
      title: t("Company_Table.Table_Telephone"),
      width: "5%",
      dataIndex: "telephone",
      render: (_, record) => record.telephone || "-",
    },
    {
      title: t("Company_Table.Table_Website"),
      width: "5%",
      dataIndex: "website",
      render: (_, record) => record.website || "-",
    },
    {
      title: () => t("Company_Table.Table_Company_Bill_Type"),
      width: "5%",
      dataIndex: "company_bill_type",
      render: (_, record) => record.company_bill_type || "-",
    },
    {
      title: () => t("Company_Table.Table_Customer_Bill_Type"),
      width: "5%",
      dataIndex: "customer_bill_type",
      render: (_, record) => record.customer_bill_type || "-",
    },
    {
      title: t("Company_Table.Table_Customer_Bill_Primary_color"),
      width: "5%",
      dataIndex: "customer_bill_primary_color",
      render: (_, record) => record.customer_bill_primary_color || "-",
    },
    {
      title: t("Company_Table.Table_Customer_Bill_Secondary_color"),
      width: "5%",
      dataIndex: "customer_bill_secondary_color",
      render: (_, record) => record.customer_bill_secondary_color || "-",
    },
    {
      title: t("Company_Table.Table_Note"),
      width: "5%",
      dataIndex: "note",
      render: (_, record) => record.note || "-",
    },
    {
      title: t("Company_Table.Table_IsActive"),
      width: "5%",
      dataIndex: "is_active",
      render: (_, record) => (
        <Switch
          value={record.is_active}
          onClick={(event) => {
            updateStatus(record, event);
          }}
        />
      ),
    },
    {
      title: t("Company_Table.Table_Edit"),
      width: "2%",
      key: "edit",
      render: (_, row) => {
        return (
          (isCompanyFullAccess || hasPermission(permissions.Customer?.CHANGE)) && (
            <Button
              type="link"
              onClick={() => {
                navigate("/company/create", {
                  state: { type: "EDIT", companyData: row },
                });
              }}
            >
              <EditOutlined />
            </Button>
          )
        );
      },
    },
    {
      title: t("Company_Table.Table_Delete"),
      width: "2%",
      key: "delete",
      render: (_, record) => {
        return (
          (isCompanyFullAccess || hasPermission(permissions.Customer?.DELETE)) && (
            <Button
              type="link"
              onClick={() => {
                setIsOpenDelete(true);
                setPrepareDeleteData(record);
              }}
            >
              <DeleteOutlined />
            </Button>
          )
        );
      },
    },
  ];

  const items = [
    {
      title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span>,
    },
    {
      title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span>,
    },
    {
      title: (
        <a href="/company" style={{ fontWeight: 600, color: "black" }}>
          {t("Title_Company")}
        </a>
      ),
    },
  ];

  const selectIsCredit = [
    {
      value: "all",
      label: "All",
    },
    {
      value: "true",
      label: "Credit",
    },
    {
      value: "false",
      label: "Not Credit",
    },
  ];
  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row>
          <Col span={24}>
            <div className="mb-2">
              <Breadcrumb items={items} />
            </div>
            <Title level={4} style={{ textWrap: "nowrap", display: "flex", gap: "7px" }}>
              <ArrowLeftOutlined style={{ fontSize: "20px" }} />
              {t("Title_Company")}
            </Title>
          </Col>
        </Row>
        <Row style={{ display: "flex", justifyContent: "left", overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={fromSearch}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              labelAlign="left"
              layout="horizontal"
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  fromSearch.submit();
                }
              }}
              initialValues={{
                search_date: [dayjs().startOf("day"), dayjs().endOf("day")],
                company_data: "all",
                company_sale: "all",
                search: undefined,
                search_credit: "all",
                search_company_id: defaultUserData?.company_id || "all",
                search_sale_id: defaultUserData?.sale_id || "all",
              }}
            >
              <Row flex={1}>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search_date" label={t("Company_Create.Company_Created_At") + ":"}>
                    <RangePicker
                      allowEmpty
                      style={{ width: "100%", minWidth: 200 }}
                      onChange={handleDateChange}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item
                    label={t("Company_Create.Company_Company") + ":"}
                    name="search_company_id"
                    key="search_company_id"
                  >
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      showSearch
                      placeholder={"Please Select Company"}
                      options={optionCompanySelection(company)}
                      filterOption={filterOption}
                      onSelect={handleChangeCompany}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item
                    label={t("Company_Create.Company_Sale") + ":"}
                    name="search_sale_id"
                    key="search_sale_id"
                    style={{ marginBottom: "1rem" }}
                  >
                    <Select
                      showSearch
                      placeholder={"Please Select Sale"}
                      options={optionSaleSelection(company)}
                      filterOption={filterOption}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search" label={t("Company_Create.Company_Search")}>
                    <Input
                      size="default"
                      placeholder={t("Company_Create.Company_Search_Placeholder")}
                      prefix={<SearchOutlined />}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item
                    style={{ marginLeft: "5px" }}
                    label={t("Company_Create.Company_IsCredit")}
                    name="search_credit"
                    key="search_credit"
                  >
                    <Select placeholder={"Please Select Credit"} options={selectIsCredit} style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row align="middle" justify="middle">
                <Col span={24} style={{ textAlign: "end" }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button type="default" onClick={() => onReset()} style={{ marginLeft: "5px" }}>
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Space direction="inline">
              {(isCompanyFullAccess || hasPermission(permissions.Company?.EXPORT)) && (
                <Button type="primary" disabled={isOpenExport} onClick={onExport}>
                  {t("BTN_Export_Excel", { ns: "buttons" })}
                </Button>
              )}
              {(isCompanyFullAccess || hasPermission(permissions.Company?.ADD)) && (
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => {
                    navigate("/company/create", {
                      state: { type: "CREATE" },
                    });
                  }}
                >
                  {t("Button_Company.BTN_Add")}
                </Button>
              )}
            </Space>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Card>
              <Row align="middle" justify="center">
                <Col span={24}>
                  {`${t("Company_Table_Header.Select")}: ${countSelect} to ${total}`}&nbsp;
                  <Link onClick={onSelectAll} target="_blank">
                    {t("Company_Table_Header.Select_All")}
                  </Link>
                </Col>
              </Row>
              <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={companyData}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                pagination={{
                  defaultPageSize: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  pageSize: search.limit,
                  total: total,
                  onChange: (page, limit) => {
                    onSearch({ ...tempSearch, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        {(isCompanyFullAccess || hasPermission(permissions.Customer?.DELETE)) && (
          <ModalDelete
            isOpen={isOpenDelete}
            isLoading={loading}
            handleCloseModal={() => {
              setIsOpenDelete(false);
            }}
            onClick={() => {
              onDeleteCompany(prepareDeleteData);
            }}
            dataDetail={prepareDeleteData}
            t={t}
          />
        )}
      </Spin>
    </>
  );
};
